import { ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { Card } from "./components/ui/card";
import { PageHead } from "./components/PageHead";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./hooks/useAuth";
import { useServerSeoValues } from "./hooks/useServerSeoValues";
import { routes } from "./lib/routes";
import { getSubdomain } from "./lib/subdomain";
import SignIn from "./pages/SignIn";
import Verify from "./pages/Verify";
import { AdventureMapBuilderLayout } from "./components/builder";
import { ExplorerLayout } from "./components/explorer";
import MapsList from "./pages/MapsList";
import MapCreate from "./pages/MapCreate";
import MapEditor from "./pages/MapEditor";
import MapViewer from "./pages/MapViewer";
import LandingPage from "./pages/LandingPage";
import LandingPageBuilder from "./pages/LandingPageBuilder";
import LandingLayoutShowcase from "./pages/LandingLayoutShowcase";
import InviteAccept from "./pages/InviteAccept";
import NotFound from "./pages/NotFound";

const subdomain = getSubdomain();

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-4">
        <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={routes.to.auth.signIn()} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-4">
        <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      </div>
    );
  }

  if (user) {
    return <Navigate to={routes.to.home()} replace />;
  }

  return <>{children}</>;
}

function RootLayout() {
  const serverSeo = useServerSeoValues();

  return (
    <>
      <PageHead {...serverSeo} />
      <Toaster position="bottom-left" />
      <Outlet />
    </>
  );
}

function AuthLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const subdomainRouter = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: routes.paths.landingPages.root, element: <LandingPage slug={subdomain!} /> },
      { path: "*", element: <Navigate to={routes.paths.landingPages.root} replace /> },
    ],
  },
]);

const appRouter = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          // Auth routes
          {
            path: routes.paths.auth.signIn,
            element: (
              <PublicRoute>
                <SignIn />
              </PublicRoute>
            ),
          },
          { path: routes.paths.auth.verify, element: <Verify /> },

          // Invite accept - handles its own auth redirect
          { path: routes.paths.invite.accept, element: <InviteAccept /> },

          // Adventure Map Builder - shared layout with single map instance
          {
            path: routes.paths.maps.root,
            element: (
              <ProtectedRoute>
                <AdventureMapBuilderLayout />
              </ProtectedRoute>
            ),
            children: [
              { index: true, element: <MapsList /> },
              { path: "new", element: <MapCreate /> },
              { path: ":id", element: <MapEditor /> },
            ],
          },

          // Landing page builder (separate from shared map)
          {
            path: routes.paths.maps.landing,
            element: (
              <ProtectedRoute>
                <LandingPageBuilder />
              </ProtectedRoute>
            ),
          },

          // Explore Map - public map viewing
          {
            path: routes.paths.maps.publicView,
            element: <ExplorerLayout />,
            children: [{ index: true, element: <MapViewer /> }],
          },
          { path: routes.paths.landingPages.bySlug, element: <LandingPage /> },
          { path: routes.paths.landingPages.layoutShowcase, element: <LandingLayoutShowcase /> },

          // Redirect home to maps
          { path: routes.paths.home, element: <Navigate to={routes.to.maps.root()} replace /> },

          // 404 catch-all
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);

function App() {
  const router = subdomain ? subdomainRouter : appRouter;
  return <RouterProvider router={router} />;
}

export default App;
